<template>
  <div class="settings-terminals-section">
    <SettingsSectionHeader
      title="Terminals"
      subtitle="Physical CRC terminal sites"
      icon="pi-building"
    >
      <template #actions>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadTerminals"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-terminals-section__body">
      <div v-if="loading && !terminals.length" class="settings-terminals-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-terminals-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadTerminals" />
        </div>
      </Message>

      <template v-else>
        <section class="settings-terminals-section__quick-add" aria-label="Quick add terminal">
          <div class="settings-terminals-section__quick-add-title">Quick add</div>
          <form class="settings-terminals-section__quick-add-form" @submit.prevent="handleQuickAdd">
            <FloatLabel variant="on" class="settings-terminals-section__quick-add-name">
              <InputText
                id="terminal-quick-name"
                v-model="quickAdd.name"
                maxlength="160"
                autocomplete="off"
                :disabled="creating"
                class="w-full rounded-xl"
              />
              <label for="terminal-quick-name" class="text-sm font-medium">
                Name <span class="text-red-500">*</span>
              </label>
            </FloatLabel>
            <Button
              type="submit"
              label="Add"
              icon="pi pi-plus"
              :loading="creating"
              :disabled="!quickAdd.name?.trim()"
            />
          </form>
        </section>

        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="terminals-show-inactive"
          search-placeholder="Search terminals..."
          :loading="loading"
        />

        <div
          v-if="!filteredTerminals.length"
          class="settings-terminals-section__empty"
        >
          <i class="pi pi-building settings-terminals-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredTerminals"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          class="settings-terminals-section__table"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable>
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="code" header="Code" sortable style="width: 7rem">
            <template #body="{ data }">
              {{ data.code || '—' }}
            </template>
          </Column>
          <Column header="Place" style="min-width: 10rem">
            <template #body="{ data }">
              <span class="text-surface-600 dark:text-surface-400 text-sm">
                {{ formatPlace(data) }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <TerminalEditModal
      ref="editModalRef"
      v-model:visible="editModalVisible"
      :saving="mutating"
      @save="handleEditSave"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import TerminalEditModal from '../modals/TerminalEditModal.vue';
import { useSettingsOrgReferenceList } from '../../composables/useSettingsOrgReferenceList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { orgReferenceApi } from '@/api/orgReferenceApi';

defineOptions({ name: 'SettingsTerminalsSection' });

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, fetchOptions, afterMutation } = useSettingsOrgReferenceList();

const terminals = ref([]);
const loading = ref(false);
const error = ref('');
const creating = ref(false);
const mutating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const editModalRef = ref(null);

const quickAdd = reactive({
  name: '',
});

const filteredTerminals = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? terminals.value.filter((terminal) => {
        const haystack = [
          terminal.name,
          terminal.code,
          terminal.city,
          terminal.state,
          terminal.country,
          isOrgReferenceActive(terminal) ? 'active' : 'inactive',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : terminals.value;
  return sortOrgReferenceRows(base);
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(terminals.value, 'terminals', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No terminals match your search.';
  if (showInactive.value) return 'No terminals found. Use quick add above to create one.';
  return 'No active terminals yet. Use quick add above to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const formatPlace = (terminal) => {
  const parts = [terminal.city, terminal.state, terminal.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const notifyMutationSuccess = (detail) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail,
    life: 2500,
  });
};

const afterMutationReload = () => afterMutation(loadTerminals);

const loadTerminals = async () => {
  loading.value = true;
  error.value = '';
  try {
    const options = await fetchOptions();
    terminals.value = Array.isArray(options?.terminals)
      ? sortOrgReferenceRows(options.terminals)
      : [];
  } catch (err) {
    error.value = err?.message || 'Failed to load terminals.';
    terminals.value = [];
  } finally {
    loading.value = false;
  }
};

const handleQuickAdd = async () => {
  const name = quickAdd.name?.trim();
  if (!name || creating.value) return;

  creating.value = true;
  try {
    await orgReferenceApi.terminals.create({ name });
    quickAdd.name = '';
    notifyMutationSuccess('Terminal created');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to create terminal.',
      life: 3500,
    });
  } finally {
    creating.value = false;
  }
};

const openEdit = (terminal) => {
  editModalRef.value?.openEdit?.(terminal);
  editModalVisible.value = true;
};

const handleEditSave = async (payload) => {
  mutating.value = true;
  try {
    await orgReferenceApi.terminals.update(payload);
    editModalVisible.value = false;
    notifyMutationSuccess('Terminal updated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to update terminal.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (terminal) => {
  confirm.require({
    header: 'Deactivate terminal',
    message: `"${terminal.name}" will be hidden from profile options. Employees linked to this terminal keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateTerminal(terminal),
  });
};

const deactivateTerminal = async (terminal) => {
  mutating.value = true;
  try {
    await orgReferenceApi.terminals.update({
      terminal_id: terminal.id,
      is_active: 0,
    });
    notifyMutationSuccess('Terminal deactivated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to deactivate terminal.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (terminal) => {
  confirm.require({
    header: 'Delete terminal',
    message: `Delete "${terminal.name}"? Employee terminal links will be cleared. This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteTerminal(terminal),
  });
};

const deleteTerminal = async (terminal) => {
  mutating.value = true;
  try {
    await orgReferenceApi.terminals.delete({ terminal_id: terminal.id });
    notifyMutationSuccess('Terminal deleted');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to delete terminal.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadTerminals();
});

watch(showInactive, () => {
  loadTerminals();
});
</script>

<style scoped>
.settings-terminals-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-terminals-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-terminals-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-terminals-section__message {
  margin: 0;
}

.settings-terminals-section__quick-add {
  padding: 1rem;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  background: var(--crc-panel-bg);
}

.settings-terminals-section__quick-add-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--crc-text-muted, var(--text-color-secondary));
  margin-bottom: 0.75rem;
}

.settings-terminals-section__quick-add-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
}

.settings-terminals-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-terminals-section__empty-icon {
  font-size: 1.5rem;
}

.settings-terminals-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .settings-terminals-section__quick-add-form {
    grid-template-columns: 1fr;
  }
}
</style>
